import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import { ChartSymbol } from '../../context/chart/chartTypes';
import useChartState from '../../context/chart/useChartState';
import useWidth from '../../hooks/useWidth';
import chartConfig from './chart.config';
// import useApi from './useChartApi';
import useMockApi from './useMockChartApi';
import { useParams } from 'react-router-dom';
import { chartSymbolToRouteParam, verifyChartSymbol } from '../../helpers';
import { Bar, IChartingLibraryWidget } from '../../charting_library';

type TVChartContainerProps = {
    initialChartSymbol: ChartSymbol;
    setOhlc: React.Dispatch<
        React.SetStateAction<{
            o: number;
            h: number;
            l: number;
            c: number;
        }>
    >;
};

const TVChartContainer: React.FC<TVChartContainerProps> = ({
    initialChartSymbol,
    setOhlc,
}) => {
    const {
        tvWidget,
        isAdvancedMode,
        isChartReady,
        setTvWidget,
        setIsChartReady,
        toggleAdvancedMode,
    } = useChartState();

    const { chartSymbol } = useParams();

    const ref = useRef<HTMLDivElement>(null);

    // const { datafeed } = useApi();
    const { datafeed } = useMockApi();

    const windowWidth = useWidth();

    useEffect(() => {
        if (!tvWidget || !isAdvancedMode || windowWidth > 768) return;

        toggleAdvancedMode();
        tvWidget?.activeChart().executeActionById('drawingToolbarAction');
    }, [isAdvancedMode, windowWidth, tvWidget, toggleAdvancedMode, setOhlc]);

    useEffect(() => {
        if (!ref.current || !datafeed) return;

        const chartSymbolParam = chartSymbol
            ? chartSymbolToRouteParam(chartSymbol, true)
            : undefined;

        const validSymbol = verifyChartSymbol(chartSymbolParam);

        const TVChartContainer = ref.current;

        const widgetOptions = chartConfig.widgetOptions(
            validSymbol ? chartSymbolParam : initialChartSymbol,
            datafeed,
            TVChartContainer
        );

        const newTvWidget: IChartingLibraryWidget = new window.TradingView.widget(
            widgetOptions
        );

        setTvWidget(newTvWidget);

        let cursorOnChart = false;

        const mouseEnterListener = () => (cursorOnChart = true);
        const mouseLeaveListener = () => (cursorOnChart = false);

        const onTickListener = (tick: Bar) => {
            const { open, high, low, close } = tick;

            if (!cursorOnChart) {
                setTimeout(() => setOhlc({ o: open, h: high, l: low, c: close }), 0);
            }
        };

        newTvWidget.onChartReady(() => {
            setIsChartReady(true);

            // updates OHLC when lastBarData is updated
            newTvWidget.subscribe('onTick', onTickListener);

            // updates OHLC when data is first loaded
            newTvWidget?.activeChart().dataReady(() => {
                // @ts-ignore
                const bars = newTvWidget?.activeChart().getSeries(0).data().bars();
                const bar = bars?.last();

                if (!bar || !bar.value) return;

                const [, o, h, l, c] = bar.value;

                setOhlc({ o, h, l, c });
            });

            // updates OHLC when symbol changes
            newTvWidget
                .activeChart()
                .onSymbolChanged()
                .subscribe(null, () => {
                    newTvWidget.activeChart().dataReady(() => {
                        // prettier-ignore
                        // @ts-expect-error
                        const bars = newTvWidget?.activeChart().getSeries(0).data().bars();
                        const bar = bars?.last();

                        if (!bar || !bar.value) return;

                        const [, o, h, l, c] = bar.value;

                        setOhlc({ o, h, l, c });
                    });
                });

            // updates OHLC when crosshair is on bar
            newTvWidget
                ?.activeChart()
                .crossHairMoved()
                .subscribe(null, (e) => {
                    const crosshairTime = e.time;
                    // @ts-expect-error
                    const bars = newTvWidget?.activeChart().getSeries(0).data().bars();
                    const searchMode = crosshairTime < bars?.first().timeMs ? 1 : -1;
                    const bar = bars?.searchByTime(crosshairTime, searchMode);

                    if (!bar || !bar.value) {
                        cursorOnChart = false;
                        return;
                    }

                    cursorOnChart = true;

                    const [, o, h, l, c] = bar.value;

                    setOhlc({ o, h, l, c });
                });
        });

        TVChartContainer.addEventListener('mouseenter', mouseEnterListener);
        TVChartContainer.addEventListener('mouseleave', mouseLeaveListener);

        return () => {
            newTvWidget.remove();
            TVChartContainer.removeEventListener('mouseenter', mouseEnterListener);
            TVChartContainer.removeEventListener('mouseleave', mouseLeaveListener);
            setTvWidget(null);
            setIsChartReady(false);
        };
    }, [
        initialChartSymbol,
        datafeed,
        chartSymbol,
        ref,
        setIsChartReady,
        setTvWidget,
        setOhlc,
    ]);

    return (
        <>
            <div
                ref={ref}
                className={clsx('TVChartContainer', { visible: isChartReady })}
                tabIndex={-1}
            />
        </>
    );
};

export default TVChartContainer;
